import { createSignal } from 'solid-js'
import { authApi } from '../api/auth.api'
import { updateUserPreferences } from '../api/endpoints/preferences.api'
import { updateWorkspaceAudioPreferences } from '../api/endpoints/workspace.api'
import type { AudioPreferences, UserPreferences } from '../api/types/generated'
import { type Locale, isLocale } from '../i18n/types'
import { detectBrowserLanguage } from '../i18n/utils/browser-language'

export type TTSProvider = 'elevenlabs' | 'deepgram' | 'cartesia' | 'grok' | 'mistral'
const STT_PROVIDERS = [
  'web_speech',
  'whisper',
  'deepgram',
  'deepgram_flux',
  'grok',
  'mistral',
] as const
export type STTProvider = (typeof STT_PROVIDERS)[number]
export type Theme = 'light' | 'dark' | 'system'
export type ResponseTiming = 'fast' | 'balanced' | 'patient'
export type LLMModel = 'haiku' | 'sonnet'
export type CompanionMode = 'voice' | 'text'
export type VoiceEngine = 'cascade' | 's2s'

export interface LabFeatures {
  idealProfiles: boolean
  dashboard: boolean
  intelligence: boolean
  aiRecruiter: boolean
  marketplace: boolean
  activity: boolean
  people: boolean
  mockInterview: boolean
  whatsapp: boolean
}

export interface CandidateLabFeatures {
  dailyWorkout: boolean
  mockInterview: boolean
  pitchGenerator: boolean
  practiceHistory: boolean
  jobTracker: boolean
  resumeScanner: boolean
  achievements: boolean
  coachingPath: boolean
  resumeAlignment: boolean
  dashboard: boolean
}

export interface FluxConfig {
  eotThreshold: number
  eagerEotThreshold: number
  eotTimeoutMs: number
}

export interface FishAudioConfig {
  speed: number
  temperature: number
  latency: 'low' | 'balanced' | 'normal'
  normalize: boolean
}

const defaultFluxConfig: FluxConfig = {
  eotThreshold: 0.85,
  eagerEotThreshold: 0,
  eotTimeoutMs: 10000,
}

const defaultFishAudioConfig: FishAudioConfig = {
  speed: 1.0,
  temperature: 0.7,
  latency: 'normal',
  normalize: true,
}

const defaultLabFeatures: LabFeatures = {
  idealProfiles: false,
  dashboard: false,
  intelligence: false,
  aiRecruiter: false,
  marketplace: false,
  activity: false,
  people: false,
  mockInterview: false,
  whatsapp: false,
}

const defaultCandidateLabFeatures: CandidateLabFeatures = {
  dailyWorkout: false,
  mockInterview: false,
  pitchGenerator: false,
  practiceHistory: false,
  jobTracker: false,
  resumeScanner: false,
  achievements: false,
  coachingPath: false,
  resumeAlignment: false,
  dashboard: false,
}

interface Preferences {
  voiceEngine: VoiceEngine
  s2sVoice: string
  ttsProvider: TTSProvider
  sttProvider: STTProvider
  theme: Theme
  autoPlay: boolean
  language: Locale
  debugMode: boolean
  fluxConfig: FluxConfig
  deepgramVoice: string
  ttsVoice: string
  fishAudioVoice: string
  fishAudioVoiceName: string
  fishAudioConfig: FishAudioConfig
  supportWidgetEnabled: boolean
  weeklyBriefOptIn: boolean
  companionMode: CompanionMode
  labFeatures: LabFeatures
  candidateLabFeatures: CandidateLabFeatures
  speechSpeed: number
  responseTiming: ResponseTiming
  llmModel: LLMModel
}

const defaultPreferences: Preferences = {
  voiceEngine: 's2s',
  s2sVoice: 'Charon',
  ttsProvider: 'cartesia',
  sttProvider: 'deepgram_flux',
  theme: 'light',
  autoPlay: true,
  language: 'en',
  debugMode: false,
  fluxConfig: defaultFluxConfig,
  deepgramVoice: 'aura-2-amalthea-en',
  ttsVoice: 'f786b574-daa5-4673-aa0c-cbe3e8534c02',
  fishAudioVoice: '',
  fishAudioVoiceName: '',
  fishAudioConfig: defaultFishAudioConfig,
  supportWidgetEnabled: true,
  weeklyBriefOptIn: false,
  companionMode: 'voice',
  labFeatures: defaultLabFeatures,
  candidateLabFeatures: defaultCandidateLabFeatures,
  speechSpeed: 1.0,
  responseTiming: 'balanced',
  llmModel: 'haiku',
}

const isSTTProvider = (value: string): value is STTProvider =>
  STT_PROVIDERS.some((provider) => provider === value)

const storedSTTProvider = (stored: string): STTProvider => {
  if (isSTTProvider(stored)) {
    return stored
  }
  return stored ? defaultPreferences.sttProvider : (stored as STTProvider)
}

type UserContext = 'hiring_manager' | 'candidate' | 'anonymous'
const [userContext, setUserContext] = createSignal<UserContext>('anonymous')
const [preferencesLoaded, setPreferencesLoaded] = createSignal(false)
const [hmHasWorkspace, setHmHasWorkspace] = createSignal(false)
const [candidateHasAccount, setCandidateHasAccount] = createSignal(false)

const THEME_HINT_KEY = 'peatch-theme-hint'
const LANGUAGE_HINT_KEY = 'peatch-language-hint'
const LANGUAGE_CHOICE_KEY = 'peatch-language-choice'
const CANDIDATE_PREFS_KEY = 'peatch-candidate-prefs'
const CANDIDATE_AUDIO_SESSION_PREFIX = 'peatch-audio-'
const LEGACY_STORAGE_KEY = 'peatch-ai-preferences'

const getInitialTheme = (): Theme => {
  try {
    const hint = localStorage.getItem(THEME_HINT_KEY)
    if (hint === 'light' || hint === 'dark' || hint === 'system') {
      return hint
    }
  } catch {
    // Ignore
  }
  return 'light'
}

const CANDIDATE_LAB_V6_KEY = 'peatch-candidate-lab-v6'

const resetCandidateLabOnce = (): boolean => {
  try {
    if (localStorage.getItem(CANDIDATE_LAB_V6_KEY)) {
      return false
    }
    const stored = localStorage.getItem(CANDIDATE_PREFS_KEY)
    if (stored) {
      const prefs = JSON.parse(stored)
      prefs.candidateLabFeatures = { ...defaultCandidateLabFeatures }
      localStorage.setItem(CANDIDATE_PREFS_KEY, JSON.stringify(prefs))
    }
    localStorage.setItem(CANDIDATE_LAB_V6_KEY, '1')
    return true
  } catch {
    return false
  }
}

const loadCandidateNonAudioPrefs = (): Partial<Preferences> => {
  try {
    const stored = localStorage.getItem(CANDIDATE_PREFS_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch {
    // Ignore
  }
  return {}
}

const saveCandidateNonAudioPrefs = (prefs: Preferences) => {
  try {
    const nonAudio = {
      theme: prefs.theme,
      language: prefs.language,
      debugMode: prefs.debugMode,
      autoPlay: prefs.autoPlay,
      supportWidgetEnabled: prefs.supportWidgetEnabled,
      candidateLabFeatures: prefs.candidateLabFeatures,
    }
    localStorage.setItem(CANDIDATE_PREFS_KEY, JSON.stringify(nonAudio))
  } catch {
    // Ignore
  }
}

const saveThemeHint = (theme: Theme) => {
  try {
    localStorage.setItem(THEME_HINT_KEY, theme)
  } catch {
    // Ignore
  }
}

const saveLanguageHint = (language: Locale) => {
  try {
    localStorage.setItem(LANGUAGE_HINT_KEY, language)
  } catch {
    // Ignore
  }
}

const saveLanguageChoice = (language: Locale) => {
  try {
    localStorage.setItem(LANGUAGE_CHOICE_KEY, language)
  } catch {
    // Ignore
  }
}

export const getExplicitLanguageChoice = (): Locale | null => {
  try {
    const choice = localStorage.getItem(LANGUAGE_CHOICE_KEY)
    if (isLocale(choice)) {
      return choice
    }
  } catch {
    // Ignore
  }
  return null
}

const debounce = <T extends (...args: unknown[]) => unknown>(fn: T, ms: number) => {
  let timer: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    clearTimeout(timer)
    timer = setTimeout(() => fn(...args), ms)
  }
}

const debouncedSyncAudioToWorkspace = debounce(async () => {
  try {
    const prefs = preferences()
    const fishAudioVoice = prefs.fishAudioVoice
    const fishAudioConfig = prefs.fishAudioConfig

    await updateWorkspaceAudioPreferences({
      engine: prefs.voiceEngine,
      s2sVoice: prefs.s2sVoice,
      sttProvider: prefs.sttProvider,
      ttsProvider: prefs.ttsProvider,
      deepgramVoice: prefs.deepgramVoice,
      ttsVoice: prefs.ttsVoice,
      fluxConfig: prefs.fluxConfig,
      speechSpeed: prefs.speechSpeed,
      responseTiming: prefs.responseTiming,
      llmModel: prefs.llmModel,
      ...(fishAudioVoice && {
        fishAudio: {
          voice: fishAudioVoice,
          voiceName: prefs.fishAudioVoiceName,
          speed: fishAudioConfig.speed,
          temperature: fishAudioConfig.temperature,
          latency: fishAudioConfig.latency,
          normalize: fishAudioConfig.normalize,
        },
      }),
    })
  } catch (err) {
    console.warn('Failed to sync audio preferences to workspace', err)
  }
}, 1000)

const syncCandidateLanguage = async () => {
  try {
    await authApi.updateCandidateProfile({ language: preferences().language })
  } catch (err) {
    console.warn('Failed to sync candidate language to backend', err)
  }
}

const debouncedSyncCandidateLanguage = debounce(syncCandidateLanguage, 1000)

const debouncedSyncUserPrefsToBackend = debounce(async () => {
  try {
    const prefs = preferences()
    const fishAudioVoice = prefs.fishAudioVoice
    const fishAudioConfig = prefs.fishAudioConfig

    const payload: UserPreferences = {
      theme: prefs.theme,
      language: prefs.language,
      debugMode: prefs.debugMode,
      autoPlay: prefs.autoPlay,
      supportWidgetEnabled: prefs.supportWidgetEnabled,
      weeklyBriefOptIn: prefs.weeklyBriefOptIn,
      companionMode: prefs.companionMode,
      labFeatures: prefs.labFeatures,
    }

    if (!hmHasWorkspace()) {
      payload.audioPreferences = {
        engine: prefs.voiceEngine,
        s2sVoice: prefs.s2sVoice,
        sttProvider: prefs.sttProvider,
        ttsProvider: prefs.ttsProvider,
        deepgramVoice: prefs.deepgramVoice,
        ttsVoice: prefs.ttsVoice,
        fluxConfig: prefs.fluxConfig,
        speechSpeed: prefs.speechSpeed,
        responseTiming: prefs.responseTiming,
        llmModel: prefs.llmModel,
        ...(fishAudioVoice && {
          fishAudio: {
            voice: fishAudioVoice,
            voiceName: prefs.fishAudioVoiceName,
            speed: fishAudioConfig.speed,
            temperature: fishAudioConfig.temperature,
            latency: fishAudioConfig.latency,
            normalize: fishAudioConfig.normalize,
          },
        }),
      }
    }

    await updateUserPreferences(payload)
  } catch (err) {
    console.warn('Failed to sync user preferences to backend', err)
  }
}, 1000)

const AUDIO_PREF_KEYS: (keyof Preferences)[] = [
  'voiceEngine',
  's2sVoice',
  'ttsProvider',
  'sttProvider',
  'deepgramVoice',
  'ttsVoice',
  'fishAudioVoice',
  'fishAudioVoiceName',
  'fishAudioConfig',
  'fluxConfig',
  'speechSpeed',
  'responseTiming',
  'llmModel',
]

const getInitialLanguage = (): Locale => {
  try {
    const hint = localStorage.getItem(LANGUAGE_HINT_KEY)
    if (isLocale(hint)) {
      return hint
    }
  } catch {
    // Ignore
  }
  return detectBrowserLanguage()
}

const [preferences, setPreferencesInternal] = createSignal<Preferences>({
  ...defaultPreferences,
  theme: getInitialTheme(),
  language: getInitialLanguage(),
})

export const getPreferences = () => preferences()
export const isPreferencesLoaded = () => preferencesLoaded()
export const getUserContext = () => userContext()

export const setUserContextEagerly = (ctx: UserContext) => setUserContext(ctx)

export const setPreferences = (updates: Partial<Preferences>) => {
  setPreferencesInternal((prev) => {
    const next = { ...prev, ...updates }

    if (updates.theme) {
      saveThemeHint(next.theme)
    }

    return next
  })

  const ctx = userContext()
  const changedKeys = Object.keys(updates) as (keyof Preferences)[]
  const hasAudioChange = changedKeys.some((k) => AUDIO_PREF_KEYS.includes(k))
  const hasNonAudioChange = changedKeys.some((k) => !AUDIO_PREF_KEYS.includes(k))

  if (ctx === 'hiring_manager') {
    if (hmHasWorkspace()) {
      if (hasAudioChange) {
        debouncedSyncAudioToWorkspace()
      }
      if (hasNonAudioChange) {
        debouncedSyncUserPrefsToBackend()
      }
    } else {
      if (hasAudioChange || hasNonAudioChange) {
        debouncedSyncUserPrefsToBackend()
      }
    }
  } else if (ctx === 'candidate') {
    if (hasNonAudioChange) {
      saveCandidateNonAudioPrefs(preferences())
    }
    if (updates.language && candidateHasAccount()) {
      debouncedSyncCandidateLanguage()
    }
  }
}

function audioPrefsToUpdate(
  audioPreferences: AudioPreferences | null | undefined
): Partial<Preferences> {
  const updates: Partial<Preferences> = {}
  if (!audioPreferences) {
    return updates
  }
  if (audioPreferences.engine === 's2s' || audioPreferences.engine === 'cascade') {
    updates.voiceEngine = audioPreferences.engine
  }
  if (audioPreferences.s2sVoice) {
    updates.s2sVoice = audioPreferences.s2sVoice
  }
  updates.sttProvider = storedSTTProvider(audioPreferences.sttProvider)
  updates.ttsProvider = audioPreferences.ttsProvider as TTSProvider
  updates.deepgramVoice = audioPreferences.deepgramVoice
  if (audioPreferences.ttsVoice) {
    updates.ttsVoice = audioPreferences.ttsVoice
  }
  if (audioPreferences.fluxConfig) {
    updates.fluxConfig = audioPreferences.fluxConfig
  }
  if (audioPreferences.speechSpeed !== undefined) {
    updates.speechSpeed = audioPreferences.speechSpeed
  }
  if (audioPreferences.responseTiming) {
    updates.responseTiming = audioPreferences.responseTiming as ResponseTiming
  }
  if (audioPreferences.llmModel) {
    updates.llmModel = audioPreferences.llmModel as LLMModel
  }
  if (audioPreferences.fishAudio) {
    updates.fishAudioVoice = audioPreferences.fishAudio.voice
    updates.fishAudioVoiceName = audioPreferences.fishAudio.voiceName
    if (audioPreferences.fishAudio.speed !== undefined) {
      updates.fishAudioConfig = {
        speed: audioPreferences.fishAudio.speed ?? defaultFishAudioConfig.speed,
        temperature: audioPreferences.fishAudio.temperature ?? defaultFishAudioConfig.temperature,
        latency:
          (audioPreferences.fishAudio.latency as FishAudioConfig['latency']) ??
          defaultFishAudioConfig.latency,
        normalize: audioPreferences.fishAudio.normalize ?? defaultFishAudioConfig.normalize,
      }
    }
  }
  return updates
}

const audioDefaults = (): Partial<Preferences> =>
  Object.fromEntries(
    AUDIO_PREF_KEYS.map((key) => [key, defaultPreferences[key]])
  ) as Partial<Preferences>

export const refreshAudioPreferencesFromWorkspace = (
  audioPreferences: AudioPreferences | null | undefined
) => {
  if (!audioPreferences) {
    setPreferencesInternal((prev) => ({ ...prev, ...audioDefaults() }))
    return
  }
  const updates = audioPrefsToUpdate(audioPreferences)
  if (Object.keys(updates).length === 0) {
    return
  }
  setPreferencesInternal((prev) => ({ ...prev, ...updates }))
}

export const initializeForHiringManager = async (
  workspaceExists: boolean,
  workspaceAudioPrefs: AudioPreferences | null | undefined,
  backendUserPrefs: UserPreferences | null | undefined
) => {
  setUserContext('hiring_manager')
  setHmHasWorkspace(workspaceExists)

  const updates: Partial<Preferences> = {
    ...audioPrefsToUpdate(workspaceAudioPrefs ?? backendUserPrefs?.audioPreferences ?? null),
  }

  if (backendUserPrefs) {
    if (backendUserPrefs.theme) {
      updates.theme = backendUserPrefs.theme as Theme
    }
    if (isLocale(backendUserPrefs.language)) {
      updates.language = backendUserPrefs.language
    }
    updates.debugMode = backendUserPrefs.debugMode ?? false
    updates.autoPlay = backendUserPrefs.autoPlay ?? true
    updates.supportWidgetEnabled = backendUserPrefs.supportWidgetEnabled ?? true
    updates.weeklyBriefOptIn = backendUserPrefs.weeklyBriefOptIn ?? false
    updates.companionMode = backendUserPrefs.companionMode === 'text' ? 'text' : 'voice'
    if (backendUserPrefs.labFeatures) {
      updates.labFeatures = { ...defaultLabFeatures, ...backendUserPrefs.labFeatures }
    }
  }

  setPreferencesInternal((prev) => {
    const next = { ...prev, ...updates }
    saveThemeHint(next.theme)
    if (backendUserPrefs?.language) {
      saveLanguageHint(next.language)
    }
    return next
  })

  await migrateLegacyPreferences()

  setPreferencesLoaded(true)
}

export interface CandidateAccountLanguage {
  language: Locale
  unconfirmed: boolean
}

export const initializeForCandidate = (
  audioPreferences: AudioPreferences | null | undefined,
  token?: string,
  account?: CandidateAccountLanguage
) => {
  setUserContext('candidate')
  setCandidateHasAccount(account !== undefined)

  const updates: Partial<Preferences> = {}

  if (audioPreferences) {
    updates.sttProvider = storedSTTProvider(audioPreferences.sttProvider)
    updates.ttsProvider = audioPreferences.ttsProvider as TTSProvider
    updates.deepgramVoice = audioPreferences.deepgramVoice
    if (audioPreferences.ttsVoice) {
      updates.ttsVoice = audioPreferences.ttsVoice
    }
    if (audioPreferences.fluxConfig) {
      updates.fluxConfig = audioPreferences.fluxConfig
    }
    if (audioPreferences.speechSpeed !== undefined) {
      updates.speechSpeed = audioPreferences.speechSpeed
    }
    if (audioPreferences.responseTiming) {
      updates.responseTiming = audioPreferences.responseTiming as ResponseTiming
    }
    if (audioPreferences.llmModel) {
      updates.llmModel = audioPreferences.llmModel as LLMModel
    }
    if (audioPreferences.fishAudio) {
      updates.fishAudioVoice = audioPreferences.fishAudio.voice
      updates.fishAudioVoiceName = audioPreferences.fishAudio.voiceName
      if (audioPreferences.fishAudio.speed !== undefined) {
        updates.fishAudioConfig = {
          speed: audioPreferences.fishAudio.speed ?? defaultFishAudioConfig.speed,
          temperature: audioPreferences.fishAudio.temperature ?? defaultFishAudioConfig.temperature,
          latency:
            (audioPreferences.fishAudio.latency as FishAudioConfig['latency']) ??
            defaultFishAudioConfig.latency,
          normalize: audioPreferences.fishAudio.normalize ?? defaultFishAudioConfig.normalize,
        }
      }
    }

    if (token) {
      try {
        sessionStorage.setItem(
          CANDIDATE_AUDIO_SESSION_PREFIX + token,
          JSON.stringify(audioPreferences)
        )
      } catch {
        // Ignore
      }
    }
  } else {
    if (token) {
      try {
        const cached = sessionStorage.getItem(CANDIDATE_AUDIO_SESSION_PREFIX + token)
        if (cached) {
          const restored: AudioPreferences = JSON.parse(cached)
          updates.sttProvider = storedSTTProvider(restored.sttProvider)
          updates.ttsProvider = restored.ttsProvider as TTSProvider
          updates.deepgramVoice = restored.deepgramVoice
          if (restored.fluxConfig) {
            updates.fluxConfig = restored.fluxConfig
          }
          if (restored.fishAudio) {
            updates.fishAudioVoice = restored.fishAudio.voice
            updates.fishAudioVoiceName = restored.fishAudio.voiceName
          }
        }
      } catch {
        // Ignore
      }
    }

    if (!updates.sttProvider) {
      updates.sttProvider = 'deepgram_flux'
      updates.ttsProvider = 'deepgram'
      updates.deepgramVoice = 'aura-2-amalthea-en'
    }
  }

  resetCandidateLabOnce()

  const candidateNonAudio = loadCandidateNonAudioPrefs()
  Object.assign(updates, candidateNonAudio)
  if (candidateNonAudio.candidateLabFeatures) {
    updates.candidateLabFeatures = {
      ...defaultCandidateLabFeatures,
      ...candidateNonAudio.candidateLabFeatures,
    }
  }
  if (account) {
    updates.language = account.unconfirmed
      ? (getExplicitLanguageChoice() ?? detectBrowserLanguage())
      : account.language
  }

  setPreferencesInternal((prev) => {
    const next = { ...prev, ...updates }
    saveThemeHint(next.theme)
    if (account || candidateNonAudio.language) {
      saveLanguageHint(next.language)
    }
    return next
  })

  if (account?.unconfirmed) {
    void syncCandidateLanguage()
  }

  setPreferencesLoaded(true)
}

export const restoreCandidateAudioFromSession = (token: string) => {
  try {
    const cached = sessionStorage.getItem(CANDIDATE_AUDIO_SESSION_PREFIX + token)
    if (cached) {
      const restored: AudioPreferences = JSON.parse(cached)
      setPreferencesInternal((prev) => ({
        ...prev,
        sttProvider: storedSTTProvider(restored.sttProvider) ?? prev.sttProvider,
        ttsProvider: (restored.ttsProvider as TTSProvider) ?? prev.ttsProvider,
        deepgramVoice: restored.deepgramVoice ?? prev.deepgramVoice,
        fluxConfig: restored.fluxConfig ?? prev.fluxConfig,
        fishAudioVoice: restored.fishAudio?.voice ?? prev.fishAudioVoice,
        fishAudioVoiceName: restored.fishAudio?.voiceName ?? prev.fishAudioVoiceName,
      }))
    }
  } catch {
    // Ignore
  }
}

const migrateLegacyPreferences = async () => {
  try {
    const stored = localStorage.getItem(LEGACY_STORAGE_KEY)
    if (!stored) {
      return
    }

    const legacy = JSON.parse(stored) as Partial<Preferences>

    await updateUserPreferences({
      theme: legacy.theme ?? 'light',
      language: legacy.language ?? 'en',
      debugMode: legacy.debugMode ?? false,
      autoPlay: legacy.autoPlay ?? true,
      supportWidgetEnabled: legacy.supportWidgetEnabled ?? true,
      labFeatures: legacy.labFeatures ?? defaultLabFeatures,
      weeklyBriefOptIn: legacy.weeklyBriefOptIn ?? false,
      companionMode: legacy.companionMode ?? 'voice',
    })

    localStorage.removeItem(LEGACY_STORAGE_KEY)
  } catch {
    console.warn('Failed to migrate legacy preferences to backend')
  }
}

export const resetPreferences = () => {
  setUserContext('anonymous')
  setPreferencesLoaded(false)
  setHmHasWorkspace(false)
  setCandidateHasAccount(false)
  setPreferencesInternal({
    ...defaultPreferences,
    theme: getInitialTheme(),
    language: getInitialLanguage(),
  })
}

export const getTTSProvider = () => preferences().ttsProvider
export const setTTSProvider = (provider: TTSProvider) => setPreferences({ ttsProvider: provider })

export const getSTTProvider = () => preferences().sttProvider
export const setSTTProvider = (provider: STTProvider) => setPreferences({ sttProvider: provider })

export const getTheme = () => preferences().theme
export const setTheme = (theme: Theme) => setPreferences({ theme })

export const getAutoPlay = () => preferences().autoPlay
export const setAutoPlay = (autoPlay: boolean) => setPreferences({ autoPlay })

export const getLanguage = (): Locale => preferences().language
export const setLanguage = (language: Locale) => {
  saveLanguageHint(language)
  saveLanguageChoice(language)
  setPreferences({ language })
}

export const getDebugMode = () => preferences().debugMode
export const setDebugMode = (debugMode: boolean) => setPreferences({ debugMode })

export const getWeeklyBriefOptIn = () => preferences().weeklyBriefOptIn
export const setWeeklyBriefOptIn = (weeklyBriefOptIn: boolean) =>
  setPreferences({ weeklyBriefOptIn })

export const getCompanionMode = () => preferences().companionMode
export const setCompanionMode = (companionMode: CompanionMode) => setPreferences({ companionMode })

export const getFluxConfig = () => preferences().fluxConfig
export const setFluxConfig = (config: Partial<FluxConfig>) =>
  setPreferences({ fluxConfig: { ...preferences().fluxConfig, ...config } })

export const getDeepgramVoice = () => preferences().deepgramVoice
export const setDeepgramVoice = (voice: string) => setPreferences({ deepgramVoice: voice })

export const getTTSVoice = () => preferences().ttsVoice
export const setTTSVoice = (voice: string) => setPreferences({ ttsVoice: voice })

export const getFishAudioVoice = () => preferences().fishAudioVoice
export const setFishAudioVoice = (voice: string) => setPreferences({ fishAudioVoice: voice })

export const getFishAudioVoiceName = () => preferences().fishAudioVoiceName
export const setFishAudioVoiceName = (name: string) => setPreferences({ fishAudioVoiceName: name })

export const getFishAudioConfig = () => preferences().fishAudioConfig
export const setFishAudioConfig = (config: Partial<FishAudioConfig>) =>
  setPreferences({ fishAudioConfig: { ...preferences().fishAudioConfig, ...config } })

export const getSupportWidgetEnabled = () => preferences().supportWidgetEnabled
export const setSupportWidgetEnabled = (enabled: boolean) =>
  setPreferences({ supportWidgetEnabled: enabled })

export const getSpeechSpeed = () => preferences().speechSpeed
export const setSpeechSpeed = (speed: number) => setPreferences({ speechSpeed: speed })

export const getResponseTiming = (): ResponseTiming => preferences().responseTiming
export const setResponseTiming = (timing: ResponseTiming) =>
  setPreferences({ responseTiming: timing })

export const getLLMModel = (): LLMModel => preferences().llmModel
export const setLLMModel = (model: LLMModel) => setPreferences({ llmModel: model })

export const getLabFeatures = () => preferences().labFeatures
export const setLabFeatures = (updates: Partial<LabFeatures>) =>
  setPreferences({ labFeatures: { ...preferences().labFeatures, ...updates } })
export const isLabFeatureEnabled = (feature: keyof LabFeatures) =>
  preferences().labFeatures[feature]

export const getCandidateLabFeatures = () => preferences().candidateLabFeatures
export const setCandidateLabFeatures = (updates: Partial<CandidateLabFeatures>) =>
  setPreferences({ candidateLabFeatures: { ...preferences().candidateLabFeatures, ...updates } })
export const isCandidateLabFeatureEnabled = (feature: keyof CandidateLabFeatures) =>
  preferences().candidateLabFeatures[feature]
